import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { QuotationLine, QuotationStatus } from '@ravago/shared/page-data/models/features/product-quotation.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../../hooks/use-path-locale';
import { SystemPageLink } from '../../system-page-link/system-page-link';

interface Props {
   quotationLine: QuotationLine;
   quoteDetailBaseRoute: string;
   packaging: string | undefined;
   isOverview?: boolean;
   showShoppingCartBtn: boolean;
   onAddToCart: () => void;
}

const QuoteCardItem = ({ label, value, highlight = false }: { label: string; value: string | undefined; highlight?: boolean }) => (
   <Box gap="sm">
      <Typography variant="small" color="subtle">
         {label}
      </Typography>
      <Typography variant={highlight ? 'small-highlight' : 'small'}>{value ?? '-'}</Typography>
   </Box>
);

const StatusBadge = ({ status }: { status: QuotationStatus }) => {
   const translate = useTranslations();

   if (status === 'ACTIVE') return <Badge variant="success">{translate('prices.quote-active')}</Badge>;
   return <Badge variant="error">{translate('prices.quote-expired')}</Badge>;
};

export const ProductPriceCardQuotationLine = ({
   quotationLine,
   quoteDetailBaseRoute,
   packaging,
   isOverview,
   showShoppingCartBtn,
   onAddToCart
}: Props) => {
   const locale = usePathLocale();
   const translate = useTranslations();
   const format = useFormatter();

   const handleAddToCart = () => onAddToCart();

   return (
      <Paper>
         <Box direction="column">
            <Box>
               <BoxItem grow={1}>
                  <Box direction="column" gap="sm">
                     <BreakpointSwitch excludeViews={['phone']}>
                        <Box gap="sm" direction="row">
                           {isOverview ? (
                              <>
                                 <SystemPageLink
                                    baseRoute={quoteDetailBaseRoute}
                                    locale={locale}
                                    type={SystemPageType.QUOTE_DETAIL}
                                    slug={quotationLine.quotation.id}
                                    underline="always"
                                    fullWidth={false}
                                 >
                                    <Typography variant="body-highlight" color="inherit">
                                       {quotationLine.quotation.name}
                                    </Typography>
                                 </SystemPageLink>
                                 <StatusBadge status={quotationLine.quotation.status} />
                              </>
                           ) : (
                              <Typography variant="body-highlight" color="primary">
                                 {format.quantity(quotationLine.quantity)}
                              </Typography>
                           )}
                        </Box>
                     </BreakpointSwitch>
                     <BreakpointSwitch includeViews={['phone']}>
                        <Box gap="sm" direction="column">
                           <StatusBadge status={quotationLine.quotation.status} />
                           <SystemPageLink
                              baseRoute={quoteDetailBaseRoute}
                              locale={locale}
                              type={SystemPageType.QUOTE_DETAIL}
                              slug={quotationLine.quotation.id}
                              underline="always"
                              fullWidth={false}
                           >
                              <Typography variant="body-highlight" color="inherit">
                                 {quotationLine.quotation.name}
                              </Typography>
                           </SystemPageLink>
                        </Box>
                     </BreakpointSwitch>
                     <Box direction="column">
                        <BreakpointSwitch includeViews={['desktop']}>
                           <Box>
                              {isOverview && <QuoteCardItem label={translate('prices.quantity')} value={format.quantity(quotationLine.quantity)} />}
                              <QuoteCardItem label={translate('prices.packaging')} value={packaging} />
                              <QuoteCardItem label={translate('prices.total')} value={format.price(quotationLine.totalPrice)} highlight />
                           </Box>
                        </BreakpointSwitch>
                        <BreakpointSwitch excludeViews={['desktop']}>
                           <Box direction="column" gap="xs">
                              {isOverview && <QuoteCardItem label={translate('prices.quantity')} value={format.quantity(quotationLine.quantity)} />}
                              <QuoteCardItem label={translate('prices.packaging')} value={packaging} />
                              <QuoteCardItem label={translate('prices.total')} value={format.price(quotationLine.totalPrice)} highlight />
                           </Box>
                        </BreakpointSwitch>
                        {quotationLine.remark && <QuoteCardItem label={translate('prices.remark')} value={quotationLine.remark} />}
                     </Box>
                  </Box>
               </BoxItem>
               <BoxItem>
                  <Box direction="row">
                     <Box direction="column" items="end" gap="xs">
                        <Typography variant="small" color="subtle">
                           {translate('prices.unit-price')}
                        </Typography>
                        <Typography variant="body-highlight" color="primary">
                           {format.unitPrice(quotationLine.unitPrice)}
                        </Typography>
                     </Box>
                     {showShoppingCartBtn && (
                        <BreakpointSwitch excludeViews={['tab-port', 'phone']}>
                           <Button leadingIcon="shopping-cart" disabled={quotationLine.quotation.status === 'EXPIRED'} onClick={handleAddToCart}>
                              {translate('prices.add-to-cart')}
                           </Button>
                        </BreakpointSwitch>
                     )}
                  </Box>
               </BoxItem>
            </Box>
            {showShoppingCartBtn && (
               <BreakpointSwitch includeViews={['tab-port', 'phone']}>
                  <Button leadingIcon="shopping-cart" fullWidth disabled={quotationLine.quotation.status === 'EXPIRED'} onClick={handleAddToCart}>
                     {translate('prices.add-to-cart')}
                  </Button>
               </BreakpointSwitch>
            )}
         </Box>
      </Paper>
   );
};
